import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Radio, RadioGroup, SelectItem, TmdxSelect } from '@ds';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { DeviceTrackingGraph } from '@/api/apis/route';
import { formatDateTimeStringToTimeZone } from '@/utils/helpers';
import { LocationTrackingFormValues } from './config';

type EditLocationTrackingFormProps = {
  graphs: DeviceTrackingGraph[] | null;
};

const BOOLEAN_OPTIONS = [
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

export const EditLocationTrackingForm = ({ graphs }: EditLocationTrackingFormProps) => {
  const { control } = useFormContext<LocationTrackingFormValues>();

  const deviceToDisplay = useWatch({ control, name: 'deviceToDisplay' });

  const selectedGraph = (graphs ?? []).find((graph) => graph.device.id === deviceToDisplay);
  const arrivalTimeString = selectedGraph?.estimatedArrivalTime ?? selectedGraph?.scheduledArrivalTime ?? null;
  const formattedArrivalTime = arrivalTimeString ? formatDateTimeStringToTimeZone(arrivalTimeString) : null;

  return (
    <div className="flex flex-col gap-6">
      <FormRow title="Enable TX Center ETA">
        <Controller
          name="displayEstimatedTimeOfArrival"
          control={control}
          render={({ field }) => (
            <RadioGroup
              orientation="horizontal"
              value={String(field.value)}
              onValueChange={(val) => field.onChange(val === 'true')}
            >
              {BOOLEAN_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value} size="md">
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
        <div className="flex items-center">
          <span className="text-m-r text-base-20">{formattedArrivalTime}</span>
        </div>
      </FormRow>
      <FormRow title="Enable Location Tracking">
        <Controller
          name="displayLocationTracking"
          control={control}
          render={({ field }) => (
            <RadioGroup
              orientation="horizontal"
              value={String(field.value)}
              onValueChange={(val) => field.onChange(val === 'true')}
            >
              {BOOLEAN_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value} size="md">
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
      </FormRow>
      <FormRow title="OCS Device for Tracking">
        <Controller
          name="deviceToDisplay"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
            <TmdxSelect
              aria-label="Select OCS Device for Tracking"
              className="w-[314px]"
              selectedKeys={value ? [value] : []}
              disallowEmptySelection
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            >
              {(graphs ?? []).map((graph) => {
                const label = graph.device.name ?? graph.device.fullSerialNumber ?? graph.device.id;
                const secondary = graph.device.fullSerialNumber ?? graph.device.serialNumber;
                return (
                  <SelectItem key={graph.device.id} textValue={label}>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-m-m">{label}</span>
                      {secondary && <span className="text-s-r text-base-20">{secondary}</span>}
                    </div>
                  </SelectItem>
                );
              })}
            </TmdxSelect>
          )}
        />
      </FormRow>
    </div>
  );
};
